<template>
  <CModal :onEsc="onCloseFile" show :to="appElem" :trapFocus="false">
    <div
      class="c-file-viewer"
      :class="{
        [`c-file-viewer--${file?.content_type}`]: !!file?.content_type,
      }"
      :style="{ background: themeVars.primary6 }"
    >
      <CFileViewerTop
        :allowNoteTaking="allowNoteTaking"
        :areAnnotationsModified="areAnnotationsModified"
        :clientType="clientType"
        :disableFullScreen="fileType === 'pdf' && !instance"
        :feedbackIntegration="feedbackIntegration"
        :file="file"
        :hideActions="hideActions"
        :hideDownload="hideDownload"
        :isInSharebox="isInSharebox"
        :isPreviewMode="isPreviewMode"
        :isPublishing="isSaving"
        :recentFiles="enabledRecents ? recentFiles : []"
        :track="track"
        @add-to-sharebox="$emit('addToSharebox', $event)"
        @click="focusPspdfkit"
        @close-file="onCloseFile"
        @download-file="onDownloadFile"
        @edit-file="onEditFile"
        @publish-file="onPublishFile"
        @search="$emit('search')"
        @select-recent-file="onSelectRecentFile"
        @show-recent-files="$emit('showRecentFiles', $event)"
        @toggle-file-info="onToggleFileInfo"
        @toggle-fullscreen="onToggleFullscreen(true)"
        @toggle-notes="$emit('toggleNotes')"
      />
      <CDrawer
        v-if="showFileInfo"
        placement="right"
        show
        :style="{
          border: `1px solid ${themeVars.primary5}`,
          'margin-top': '8px',
          'margin-bottom': '8px',
          'border-radius': '8px 0px 0px 8px',
        }"
        width="404"
        @update:show="showFileInfo = $event"
      >
        <CDrawerContent>
          <CButton
            class="component-drawer__close-button absolute top-[24px] right-[404px] z-12 rounded mr-1 h-11 w-11"
            :style="{ background: themeVars.primary5, border: `1px solid ${themeVars.primary}` }"
            @click="showFileInfo = false"
          >
            <template #icon>
              <CIcon icon="close" size="large" />
            </template>
          </CButton>
          <slot :file="file" name="file-info" />
        </CDrawerContent>
      </CDrawer>

      <div v-if="!file" class="c-file-viewer__loader-container flex items-center justify-center">
        <CSpin :color="themeVars.primary" size="large" />
      </div>
      <div
        v-else-if="('status' in file && file.status !== 'viewable') || fileType === 'ar' || !file?.content_url"
        class="c-file-viewer__content-status-container"
      >
        <CErrorFullScreen
          v-if="file.status !== 'viewable'"
          :description="t('canvasUI.components.fileViewer.fileNotReady.description')"
          fit-parent
          icon="hourglass-clock"
          :title="t('canvasUI.components.fileViewer.fileNotReady.title')"
        />
        <CErrorFullScreen
          v-else-if="!file.content_url"
          :description="t('canvasUI.components.fileViewer.missingFileUrl.description')"
          fit-parent
          icon="face-worried"
          :title="t('canvasUI.components.fileViewer.missingFileUrl.title')"
        />
        <div v-else-if="fileType === 'ar' && supportsAr" class="w-full h-full flex justify-center items-center">
          <a :href="file.content_url" rel="ar">
            <CImage class="w-full h-auto object-cover" object-fit="cover" :src="file.thumbnail_url" />
          </a>
        </div>
        <CErrorFullScreen
          v-else-if="fileType === 'ar'"
          :description="t('canvasUI.components.fileViewer.arContentNotAvailable.description')"
          fit-parent
          icon="face-meh"
          :title="t('canvasUI.components.fileViewer.arContentNotAvailable.title')"
        >
          <div class="block w-full min-h-[125px] max-w-lg hover:opacity-75 focus:opacity-75 transition-opacity">
            <CImage
              class="w-full h-full object-cover"
              :img-props="{ class: 'max-w-full max-h-full object-cover' }"
              object-fit="cover"
              :src="file.thumbnail_url"
            />
          </div>
        </CErrorFullScreen>
      </div>
      <template v-else-if="fileType === 'pdf'">
        <div ref="pdfViewerEl" class="c-file-viewer__pdf-container overflow-auto" />

        <CFileViewerPageDrawer
          v-if="showPageMetadata && instanceMetadataTemplate"
          :fileMetadata="file.metadata"
          :instanceMetadataTemplate="instanceMetadataTemplate"
          :isLoadingInstanceMetadataTemplate="isLoadingInstanceMetadataTemplate"
          :isSavingPagesMetadata="isSavingPagesMetadata"
          :pageCount="totalPageCount"
          :pageIndex="currentPageIndex"
          :pagesImageUrls="file.content_thumbnails"
          @close="showPageMetadata = false"
          @save="emit('savePagesMetadata', $event)"
        />

        <CLinkAnnotationPopover
          v-if="showLinkAnnotationPopover && clientType === 'admin' && !isOverlay"
          :annotation="currentLinkAnnotation"
          :fetch-file-by-id="fetchFileById"
          :select-content="selectContent"
          @close="onCloseLinkAnnotationPopover"
          @save="onSaveLinkAnnotation"
        />
        <CFileViewerBottomPdf
          ref="bottomBarRef"
          :clientType="clientType"
          :file="file"
          :fileViewerSettings="fileViewerSettings"
          :hasHotspotsOnCurrentPage="hasHotspotsOnCurrentPage"
          :hideActions="hideActions"
          :is-fullscreen="isFullscreen"
          :isAnnotationVisibilityEnabled="isAnnotationVisibilityEnabled"
          :isAnnotationsSelected="isAnnotationOpen"
          :isHotspotToggleEnabled="launchDarkly.enable_hotspot_visibility ?? false"
          :isLinkAnnotationSelected="isLinkAnnotationOpen"
          :isOverlay="isOverlay"
          :isSearchSelected="isSearchOpen"
          :isTextAnnotationSelected="isTextAnnotationOpen"
          :isThumbnailsSelected="isThumbnailsOpen"
          :page="pageNumber"
          :rootElement="pdfViewerEl"
          :selectedAnnotationType="selectedAnnotationType"
          :totalPages="totalPageCount"
          @click="focusPspdfkit"
          @exit-fullscreen="onPdfFullscreen()"
          @fit-mode-toggle="onPdfFitModeToggle"
          @search-file="onSearchInFile"
          @select-annotation="onSelectAnnotation"
          @set-page="setPageIndex"
          @toggle-annotation-visibility="onToggleAnnotationVisibility"
          @toggle-annotations="onToggleAnnotations"
          @toggle-link-annotation="onToggleLinkAnnotation"
          @toggle-text-annotation="onToggleTextAnnotation"
          @toggle-thumbnails="onToggleThumbnails"
          @zoom-in="onZoomIn"
          @zoom-out="onZoomOut"
        />
      </template>
      <div v-else-if="fileType === 'web'" class="flex c-file-viewer__web-container overflow-auto">
        <iframe
          class="c-file-viewer__web-container__app w-full h-full border-0"
          :src="file.content_url"
          :title="file.name"
        />
      </div>

      <CVideo
        v-else-if="fileType === 'video'"
        ref="videoPlayerEl"
        class="c-file-viewer__video"
        :current-time="videoCurrentTime"
        :options="{
          fluid: false,
          autoplay: true,
          sources: [
            {
              src: file.content_url,
            },
          ],
        }"
      />

      <div
        v-else-if="fileType === 'image'"
        class="c-file-viewer__image-container w-full px-4 pb-10 h-full flex items-center justify-center overflow-auto"
      >
        <CImage
          :alt="file.name"
          class="c-file-viewer__image"
          :img-props="{ class: 'max-w-full' }"
          :src="file.content_url"
        />
      </div>
    </div>
  </CModal>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch, inject, h, type Ref, type ComputedRef, type WatchStopHandle } from 'vue'
import { useI18n, type I18n } from 'vue-i18n'
import type { feedbackIntegration as FeedbackIntegration } from '@sentry/vue'
import type { Instance, ViewState } from 'pspdfkit'

import {
  FileContentTypeEnum as FileContentType,
  type File,
  type MiniPage,
  type FileRetrieve,
  type MetadataTemplate,
} from '@openapi'
import type { RecentFile } from '@lib/types/recentFiles.types'
import useThemeVars from '@lib/composables/useThemeVars'
import CModal from '@lib/components/CModal/CModal.vue'
import CImage from '@lib/components/CImage/CImage.vue'
import CSpin from '@lib/components/CSpin/CSpin.vue'
import CButton from '@lib/components/CButton/CButton.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CVideo from '@lib/components/CVideo/CVideo.vue'
import CFileViewerTop from '@lib/components/CFileViewer/CFileViewerTop.vue'
import CErrorFullScreen from '@lib/components/CError/CErrorFullScreen.vue'
import CFileViewerBottomPdf from '@lib/components/CFileViewer/pdf/CFileViewerBottom.pdf.vue'
import CFileViewerCloseDialogContent from '@lib/components/CFileViewer/CFileViewerCloseDialogContent.vue'
import CLinkAnnotationPopover from '@lib/components/CFileViewer/pdf/CLinkAnnotationsPopover.pdf.vue'
import CDrawer from '@lib/components/CDrawer/CDrawer.vue'
import CDrawerContent from '@lib/components/CDrawerContent/CDrawerContent.vue'
import CFileViewerPageDrawer from '@lib/components/CFileViewer/pdf/CFileViewerPageDrawer.pdf.vue'
import { CLIENT_TYPE } from '@lib/constants/cdp.const'
import { EMITS } from '@lib/components/CFileViewer/CFileViewer.types'
import usePdfFileViewer from '@lib/components/CFileViewer/pdf/CFileViewer.pdf.use'
import usePdfEvents from '@lib/components/CFileViewer/pdf/CFileViewer.events.pdf.use'
import type { AnnotationType } from '@lib/components/CFileViewer/CFileViewer.types'
import type { PitcherFile } from '@lib/types/file.types'
import useConfirmation from '@lib/composables/useConfirmation'
import type { SupportedLocale } from '@lib/types/i18n.types'
import { requestFullscreen } from '@lib/util/fullscreen.util'
import type { OrganizationSettings } from '@lib/types/organizationSettings.types'
import type { InstanceSettings } from '@lib/types/instanceSettings.types'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import useOpenFileStack from '@lib/composables/openFileStack.use.ts'

const props = withDefaults(
  defineProps<{
    file: PitcherFile | null
    initialPageNumber?: MiniPage['page_index']
    initialSearch?: string
    isPublishing?: boolean
    isSavingPagesMetadata?: boolean
    isLoadingInstanceMetadataTemplate?: boolean
    i18n: I18n
    hideActions?: boolean
    hideDownload?: boolean
    assetsPrefix?: string
    clientType?: (typeof CLIENT_TYPE)[keyof typeof CLIENT_TYPE]
    selectContent?: (fileId?: FileRetrieve['id'], pageIndex?: number) => Promise<FileRetrieve | null>
    appLocale?: SupportedLocale
    context?: Record<string, any>
    feedbackIntegration?: typeof FeedbackIntegration
    recentFiles?: RecentFile[]
    track?: (eventName: string, payload: any) => void
    allowNoteTaking?: boolean
    fetchFileById?: (fileId: File['id']) => Promise<FileRetrieve>
    isOverlay?: boolean
    videoCurrentTime?: number
    instanceMetadataTemplate?: MetadataTemplate | null
    isInSharebox?: boolean
    isPreviewMode?: boolean
  }>(),
  {
    file: null,
    initialPageNumber: 1,
    initialSearch: '',
    isPublishing: true,
    hideActions: false,
    hideDownload: false,
    assetsPrefix: '/',
    clientType: CLIENT_TYPE.impact,
    allowNoteTaking: false,
    isOverlay: false,
  },
)

const emit = defineEmits(EMITS)

const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
  'pitcherSettings',
  computed(() => ({})),
)

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const enabledRecents = computed(() => pitcherSettings.value?.enable_recents ?? false)

const SAVE_ANNOTATIONS_ON_EXIT_STORAGE_KEY = 'save_files_annotations_on_exit'
const supportsAr = ((): boolean => {
  const a = document.createElement('a')
  return a.relList && a.relList.supports('ar')
})()
const { t } = useI18n()
const themeVars = useThemeVars()
const confirmation = useConfirmation()
const showPageMetadata = ref(false)

const fileType = computed(() => props.file?.content_type)
const isAdminAndAnnotationsModified = computed(
  () =>
    props.clientType === 'admin' &&
    areAnnotationsModified.value &&
    pitcherSettings.value.file_annotations_mode === 'admin',
)
const isImpactAndAnnotationsModified = computed(
  () =>
    props.clientType === 'impact' &&
    areAnnotationsModified.value &&
    pitcherSettings.value.file_annotations_mode === 'personal' &&
    launchDarkly.value.allow_saving_annotations_in_personal_layer,
)
const videoPlayerEl = ref<InstanceType<typeof CVideo> | null>(null)
const pdfViewerEl: Ref<HTMLElement | null> = ref(null)
const bottomBarRef = ref<InstanceType<typeof CFileViewerBottomPdf> | null>(null)
const onEditFile = () => {
  emit('editFile')
}

const onShowPageMetadata = (pageIndex: number) => {
  showPageMetadata.value = true
  setPageIndex(pageIndex + 1)
}

const isSavingInstance = ref(false)
const showFileInfo = ref(false)

const isSaving = computed(() => isSavingInstance.value || props.isPublishing)

function onToggleFileInfo() {
  showFileInfo.value = !showFileInfo.value
}

async function onCloseFile(callback: () => void = closeFile) {
  /* Check if the closed file and the stacked opened file are the same,
   ** if yes, bypass unloading and reloading the file again
   */
  const { fileStack, popFromStack } = useOpenFileStack()
  const isSameFile = fileStack.value.length > 0 && fileStack.value[fileStack.value.length - 1].fileId === props.file?.id
  if (isSameFile) {
    popFromStack()
  }
  if (isAdminAndAnnotationsModified.value) {
    confirmation.warning({
      hasExit: true,
      title: t('canvasUI.components.fileViewer.adminCloseDialog.title'),
      content: t('canvasUI.components.fileViewer.adminCloseDialog.description'),
      confirmText: t('canvasUI.components.fileViewer.adminCloseDialog.publish'),
      cancelText: t(t('canvasUI.components.fileViewer.adminCloseDialog.cancel')),
      exitText: t('canvasUI.components.fileViewer.adminCloseDialog.close'),
      onExit: () => {
        callback()
      },
      onConfirm: async () => {
        await publishFile()
        callback()
      },
    })
  } else if (isImpactAndAnnotationsModified.value) {
    const rawValue = localStorage.getItem(SAVE_ANNOTATIONS_ON_EXIT_STORAGE_KEY)
    const previousSaveAnnotationsOption = rawValue ? JSON.parse(rawValue) : null

    if (previousSaveAnnotationsOption) {
      if (previousSaveAnnotationsOption === 'save') {
        await publishFile()
      }
      callback()

      return
    }

    const rememberForNextTime = ref(true)

    confirmation.warning({
      hasExit: true,
      title: t('canvasUI.components.fileViewer.closeDialog.title'),
      content: () =>
        h(CFileViewerCloseDialogContent, {
          rememberForNextTime: rememberForNextTime.value,
          'onUpdate:rememberForNextTime': (value: boolean) => (rememberForNextTime.value = value),
        }),
      confirmText: t('canvasUI.components.fileViewer.closeDialog.publish'),
      cancelText: t(t('canvasUI.components.fileViewer.closeDialog.cancel')),
      exitText: t('canvasUI.components.fileViewer.closeDialog.close'),
      onExit: async () => {
        if (rememberForNextTime.value)
          localStorage.setItem(SAVE_ANNOTATIONS_ON_EXIT_STORAGE_KEY, JSON.stringify('discard'))

        callback()
      },
      onConfirm: async () => {
        if (rememberForNextTime.value)
          localStorage.setItem(SAVE_ANNOTATIONS_ON_EXIT_STORAGE_KEY, JSON.stringify('save'))
        await publishFile()
        callback()
      },
    })
  } else {
    callback()
  }
}

function onPublishFile() {
  if (props.clientType === 'admin')
    confirmation.warning({
      hasExit: false,
      title: t('canvasUI.components.fileViewer.publishConfirmation.title'),
      content: t('canvasUI.components.fileViewer.publishConfirmation.description'),
      confirmText: t('canvasUI.components.fileViewer.publishConfirmation.publish'),
      cancelText: t(t('canvasUI.components.fileViewer.publishConfirmation.cancel')),
      onConfirm: () => publishFile(),
    })
  else if (props.clientType === 'impact') publishFile()
}

async function publishFile() {
  try {
    isSavingInstance.value = true
    await instance.value?.save()
    resetAnnotationChanges()
    const instantJson = await instance.value?.exportInstantJSON()
    console.info('[CFileViewer.vue] instantJson: ', instantJson)
    const cleanedJson = instantJson
      ? JSON.parse(
          JSON.stringify(instantJson)
            .replace(/(?:1\.0)+<body>/g, '<body>')
            .trim(),
        )
      : null

    console.info('[CFileViewer.vue] cleanedJson: ', cleanedJson)
    emit('publishFile', props.file?.id, cleanedJson)
  } catch (error: any) {
    throw new Error(error)
  } finally {
    console.info('[CFileViewer.vue] published')
    isSavingInstance.value = false
  }
}

async function onSelectRecentFile(payload: { fileId: File['id']; pageIndex?: number }) {
  if (isAdminAndAnnotationsModified.value) {
    onCloseFile(() => emit('openFile', payload.fileId, payload.pageIndex))
  } else {
    emit('openFile', payload.fileId, payload.pageIndex)
  }
}

const {
  viewPdf,
  onPdfFullscreen,
  updateFileViewerSettings,
  cleanupPspdfKit,
  onSaveLinkAnnotation,
  onCloseLinkAnnotationPopover,
  onToggleLinkAnnotation,
  resetAnnotationChanges,
  isSearchOpen,
  isThumbnailsOpen,
  isTextAnnotationOpen,
  isLinkAnnotationOpen,
  isAnnotationOpen,
  areAnnotationsModified,
  selectedAnnotationType,
  currentPageIndex,
  isSwitchingInteractionMode,
  fileViewerSettings,
  isFullscreen,
  showLinkAnnotationPopover,
  currentLinkAnnotation,
  hasHotspotsOnCurrentPage,
} = usePdfFileViewer({
  emit,
  pdfViewerEl,
  bottomBarRef,
  i18n: props.i18n,
  appLocale: props.appLocale,
  actions: { onCloseFile, onShowPageMetadata },
  assetsPrefix: props.assetsPrefix,
  clientType: props.clientType,
  context: props.context,
  pitcherSettings,
  launchDarkly,
  fileId: computed(() => props.file?.id),
  fetchFileById: props.fetchFileById,
})

let unwatchFile: WatchStopHandle | null = null
const appElem = document.getElementById('app')
let pspdfkitRoot: HTMLElement | null = null
const instance = ref<Instance>()
const totalPageCount = computed(() => instance.value?.totalPageCount ?? 0)
const pageNumber = computed(() => (currentPageIndex.value ? currentPageIndex.value + 1 : 1))
const initialPageIndex = computed(() => (props.initialPageNumber ? props.initialPageNumber - 1 : 0))

function closeFile() {
  pspdfkitRoot?.removeEventListener('click', closeSharingPopover)
  pspdfkitRoot?.removeEventListener('click', closeAnnotationsDropdown)
  cleanupPspdfKit()

  emit('closeFile', { file: props.file })
}

const setPageIndex = (pageNumber: number) => {
  instance.value?.setViewState(
    (viewState) => viewState.set('currentPageIndex', pageNumber - 1), // 0-based indexing
  )
  focusPspdfkit()
}

const focusPspdfkit = () => {
  pspdfkitRoot?.focus()
}

const onSelectAnnotation = (annotationType: Exclude<AnnotationType, 'text'>) => {
  selectedAnnotationType.value = annotationType
  onToggleAnnotations(true)
}

const onToggleAnnotations = async (forceToggle?: boolean) => {
  isAnnotationOpen.value = forceToggle ?? !isAnnotationOpen.value
  isTextAnnotationOpen.value = false

  const interactionMap: Record<Exclude<AnnotationType, 'text'>, ViewState['interactionMode']> = {
    drawing: 'INK',
    freeformHighlight: 'INK',
    textHighlight: 'TEXT_HIGHLIGHTER',
    eraser: 'INK_ERASER',
    link: 'LINK',
  }

  if (selectedAnnotationType.value === 'freeformHighlight' || selectedAnnotationType.value === 'drawing')
    instance.value?.setCurrentAnnotationPreset(selectedAnnotationType.value)

  isSwitchingInteractionMode.value = isAnnotationOpen.value

  instance.value?.setViewState(
    (viewState) => viewState.set('interactionMode', 'INK_ERASER'), // HACK: fix the PSPDFKit bug of not being able to switch to annother preset while in the same interaction mode
  )

  isAnnotationOpen.value
    ? instance.value?.setViewState((viewState) =>
        viewState.set('interactionMode', isAnnotationOpen.value ? interactionMap[selectedAnnotationType.value] : null),
      )
    : instance.value?.setEditingAnnotation(null)

  isSwitchingInteractionMode.value = false
}

const onToggleTextAnnotation = () => {
  isTextAnnotationOpen.value = !isTextAnnotationOpen.value
  isAnnotationOpen.value = false
  isSearchOpen.value = false
  isLinkAnnotationOpen.value = false

  instance.value?.setCurrentAnnotationPreset('text') //Note: the text annotationsPreset is lost for some reason

  isTextAnnotationOpen.value
    ? instance.value?.setViewState((viewState) =>
        viewState.set('interactionMode', isTextAnnotationOpen.value ? 'TEXT' : null),
      )
    : instance.value?.setEditingAnnotation(null)
}

const onSearchInFile = () => {
  isSearchOpen.value = !isSearchOpen.value
  isTextAnnotationOpen.value = false
  isLinkAnnotationOpen.value = false

  if (isSearchOpen.value) {
    instance.value?.setEditingAnnotation(null)
    // Focus search input after opening search
    setTimeout(() => {
      const shadowDomRoot = document.querySelector('.PSPDFKit-Container')?.shadowRoot
      const searchInput = shadowDomRoot?.querySelector('.PSPDFKit-Search-Form-Input input') as HTMLInputElement | null
      searchInput?.focus()
    }, 0)
  }
  instance.value?.setViewState((viewState) => viewState.set('interactionMode', isSearchOpen.value ? 'SEARCH' : null))
}

const onZoomIn = () => {
  instance.value?.setViewState((viewState) => viewState.zoomIn())
}

const onZoomOut = () => {
  instance.value?.setViewState((viewState) => viewState.zoomOut())
}

const onPdfFitModeToggle = () => {
  const currentZoom = instance.value?.viewState.zoom
  const zoom: typeof currentZoom =
    currentZoom === 'FIT_TO_WIDTH' || typeof currentZoom === 'number' ? 'FIT_TO_VIEWPORT' : 'FIT_TO_WIDTH'

  instance.value?.setViewState((viewState) => viewState.set('zoom', zoom))

  updateFileViewerSettings({ fitMode: zoom === 'FIT_TO_VIEWPORT' ? 'viewport' : 'width' })
}

const onToggleThumbnails = () => {
  isThumbnailsOpen.value = !isThumbnailsOpen.value

  instance.value?.setViewState((viewState) => {
    return viewState.set('interactionMode', null).set('sidebarMode', isThumbnailsOpen.value ? 'THUMBNAILS' : null)
  })
}

const onDownloadFile = async () => {
  emit('downloadFile', props.file, instance.value)
}

const closeSharingPopover = (event: Event) => {
  const sharingPopover = document.querySelector('.c-file-viewer-share__popover')

  if (event.target && sharingPopover && !sharingPopover?.contains(event.target as Node)) {
    const popoverTriggerButton = document.querySelector('.c-file-viewer-share__trigger-button')

    if (popoverTriggerButton instanceof HTMLElement) {
      popoverTriggerButton.click()
    }
  }
}

const closeAnnotationsDropdown = (event: Event) => {
  const annotationsDropdown = document.querySelector('.c-file-viewer-bottom__annotation-dropdown')

  if (event.target && annotationsDropdown && !annotationsDropdown?.contains(event.target as Node)) {
    const triggerButton = document.querySelector('.c-file-viewer-bottom__annotation-dropdown-trigger')

    if (triggerButton instanceof HTMLElement) {
      triggerButton.click()
    }
  }
}

function onToggleFullscreen(forceEnter?: boolean) {
  switch (fileType.value) {
    case 'pdf':
      onPdfFullscreen(forceEnter)
      break
    case 'video':
      // Note: using videojs fullscreen api
      videoPlayerEl.value?.videoPlayer?.requestFullscreen()
      break
    case 'web':
      requestFullscreen(document.querySelector<HTMLElement>('.c-file-viewer__web-container__app'))
      break
    case 'image':
    case 'ar':
      break
    default:
      console.warn(`File type ${fileType.value} is not supported yet.`)
      break
  }
}

async function viewFile() {
  switch (fileType.value) {
    case 'pdf':
      try {
        instance.value = (await viewPdf(props.file, initialPageIndex.value)) ?? undefined

        if (import.meta.env.MODE === 'development') console.info('PSPDFKit instance: ', instance.value)

        usePdfEvents(instance.value, emit, initialPageIndex.value)
        if (props.initialSearch) {
          instance.value?.startUISearch(props.initialSearch)

          setTimeout(() => {
            onSearchInFile()
          }, 100)
        }

        const shadowDomRoot = document.querySelector('.PSPDFKit-Container')?.shadowRoot
        pspdfkitRoot = shadowDomRoot?.querySelector('.PSPDFKit-Root') ?? null

        pspdfkitRoot?.addEventListener('click', closeSharingPopover)
        pspdfkitRoot?.addEventListener('click', closeAnnotationsDropdown)

        focusPspdfkit()

        // Set initial annotation visibility classes with a delay to ensure container is ready
        setTimeout(updatePdfContainerClasses, 500)
      } catch (error) {
        console.warn(`[CFileViewer.vue] ${error}`)
      }
      break
    case 'web':
      document.querySelector<HTMLIFrameElement>('.c-file-viewer__web-container__app')?.focus()
      break
    case 'image':
    case 'ar':
      break
    default:
      console.warn(`File type ${fileType.value} is not supported yet.`)
      break
  }
}

// Annotation visibility toggle for reps
const isAnnotationVisibilityEnabled = ref(false)

const onToggleAnnotationVisibility = () => {
  isAnnotationVisibilityEnabled.value = !isAnnotationVisibilityEnabled.value
  updatePdfContainerClasses()
}

const updatePdfContainerClasses = () => {
  // Early return if LaunchDarkly flag is disabled - avoid all calculations
  if (!launchDarkly.value?.enable_hotspot_visibility) {
    return
  }

  const container = document.querySelector('.PSPDFKit-Container')
  if (!container) {
    setTimeout(updatePdfContainerClasses, 100)
    return
  }

  const shadowRoot = container.shadowRoot
  if (!shadowRoot) {
    setTimeout(updatePdfContainerClasses, 100)
    return
  }

  // Remove existing hotspot styles
  const existingStyle = shadowRoot.querySelector('#hotspot-styles')
  if (existingStyle) {
    existingStyle.remove()
  }

  // Simple condition: should we show hotspots?
  const shouldShowHotspots =
    props.clientType === 'admin' || (props.clientType === 'impact' && isAnnotationVisibilityEnabled.value)

  // If yes, inject the CSS into shadow DOM
  if (shouldShowHotspots) {
    const styleElement = document.createElement('style')
    styleElement.id = 'hotspot-styles'

    styleElement.textContent = `
      .PSPDFKit-Link-Annotation:not(.PSPDFKit-Annotation-Selected):not(.PSPDFKit-Annotation-Focused) {
        visibility: visible !important;
        background-color: color-mix(in srgb, var(--info3) 25%, transparent) !important;
        border: 2px solid var(--info) !important;
        cursor: pointer !important;
      }
    `

    shadowRoot.appendChild(styleElement)
  }
}

unwatchFile = watch(
  () => props.file,
  async (file) => {
    await nextTick()

    const isVideoReady = fileType.value === FileContentType.VIDEO && videoPlayerEl.value
    const isAnyOtherType = fileType.value !== FileContentType.VIDEO

    if (file && (isVideoReady || isAnyOtherType)) {
      unwatchFile?.()
      await viewFile()
    }
  },
  { immediate: true },
)
</script>

<style lang="scss" scoped>
.c-file-viewer {
  height: 100vh;
  height: 100svh;
  width: 100%;

  &--pdf {
    display: grid; /*fix for safari*/
    grid-template-rows: 1fr minmax(0, calc(100svh - 76px)) 76px; /*fix for safari*/
  }

  &__loader-container,
  &__content-status-container,
  &__image-container,
  &__web-container,
  &__video {
    height: calc(100svh - 76px); // offset for bottom bar
  }
}
</style>
